// src/algorithms/error-diffusion/_kernels.ts
//
// Each kernel: list of {dx, dy, w} where w is the fraction of error to push to
// the neighbour at (x+dx, y+dy). dx >= 0 if dy === 0, dy > 0 only otherwise.
// Sum of weights == 1 (within rounding).

export interface Kernel { divisor: number; cells: { dx: number; dy: number; w: number }[] }

const k = (divisor: number, rows: (number | null)[][]): Kernel => {
  // rows: each row's weights, where the position of `null` sentinel marks the
  // current pixel. Cells to the left of sentinel on the first row are ignored
  // (already-processed neighbours). Convention: sentinel only appears on row 0.
  const cells: Kernel['cells'] = [];
  for (let dy = 0; dy < rows.length; dy++) {
    const row = rows[dy]!;
    const center = row.indexOf(null);
    for (let i = 0; i < row.length; i++) {
      if (i === center) continue;
      if (dy === 0 && i < center) continue; // already-processed
      const w = row[i];
      if (typeof w !== 'number' || w === 0) continue;
      const dx = i - center;
      cells.push({ dx, dy, w });
    }
  }
  return { divisor, cells };
};

// Pixel at sentinel `null`. Weight matrices below match common references.

export const KERNELS: Record<string, Kernel> = {
  'floyd-steinberg': k(16, [
    [null, 7],
    [3, 5, 1],
  ]),
  'false-floyd-steinberg': k(8, [
    [null, 3],
    [3, 2],
  ]),
  'atkinson': k(8, [
    [null, 1, 1],
    [1, 1, 1, 0],
    [0, 1, 0, 0],
  ]),
  'jarvis': k(48, [
    [null, 7, 5],
    [3, 5, 7, 5, 3],
    [1, 3, 5, 3, 1],
  ]),
  'stucki': k(42, [
    [null, 8, 4],
    [2, 4, 8, 4, 2],
    [1, 2, 4, 2, 1],
  ]),
  'burkes': k(32, [
    [null, 8, 4],
    [2, 4, 8, 4, 2],
  ]),
  'sierra': k(32, [
    [null, 5, 3],
    [2, 4, 5, 4, 2],
    [0, 2, 3, 2, 0],
  ]),
  'sierra-two-row': k(16, [
    [null, 4, 3],
    [1, 2, 3, 2, 1],
  ]),
  'sierra-lite': k(4, [
    [null, 2],
    [1, 1, 0],
  ]),
  'stevenson-arce': k(200, [
    [null, 0, 32, 0, 0],
    [12, 0, 26, 0, 30, 0, 16],
    [0, 12, 0, 26, 0, 12, 0],
    [5, 0, 12, 0, 12, 0, 5],
  ]),
};
